import {CancelToken} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {DropdownFetcher, DropdownFetchResult} from '../../dropdowns/source/asyncDropdownSource';
import {FilterRow} from '../common/types';

/*
 * Types.
 */

export type FilterRowOf<T> = (item: T) => FilterRow;
type DropdownSourceFilter<T> = (items: ReadonlyArray<T>, query: string) => ReadonlyArray<T>;

export interface FilterDropdownResults<T> {
  items: ReadonlyArray<T>;
  rowCount: number;
  query: string;
  canLoadMore: boolean;
  loadMoreAsync?(cancelToken: CancelToken): Promise<FilterDropdownResults<T>>;
}

export interface FilterDropdownSource<T> {
  searchAsync(text: string, cancelToken: CancelToken): Promise<FilterDropdownResults<T>>;
}

/*
 * Functions.
 */

/**
 * Uses a fetcher to create a filter dropdpown source, which can be used
 * to load filterDropdown results.
 *
 * @param fetcher DropdownFetcher
 * @returns FilterDropdownSource, which is able to load results. FilterDropdownResults
 * contain functions to load more results in addition to the currently loaded items.
 */
export function computeFilterDropdownSourceAsync<T, U = any>(
  fetcher: DropdownFetcher<T, U>,
): FilterDropdownSource<T> {
  const searchAsync = async (query: string, cancelToken: CancelToken) => {
    const result = await fetcher(query, [], cancelToken, undefined);

    return computeDropdownResultAsync(fetcher, result, query);
  };

  return {searchAsync};
}

/**
 * Uses a an array of tiems to create a filter dropdpown source, which can be used
 * to load filterDropdown results. Mostly a convinence function that when paired with
 * the async dropdown source above allows us to make components that can handle both
 * sync and async fetching with the same model.
 *
 * @param items Array of items
 * @param rowFilter A filtering function to filter the items based on a query string
 * @returns FilterDropdownSource, which is able to load results. FilterDropdownResults
 * contain functions to load more results in addition to the currently loaded items. In
 * the case of a sync dropdown source canLoadMore will always be false.
 */
export function computeFilterDropdownSourceSync<T>(
  items: ReadonlyArray<T>,
  rowFilter: DropdownSourceFilter<T>,
): FilterDropdownSource<T> {
  const searchAsync = async (query: string) => {
    const filteredItems = rowFilter(items, query);

    return {
      canLoadMore: false,
      items: filteredItems,
      query,
      rowCount: filteredItems.length,
    };
  };

  return {searchAsync};
}

/*
 * Helpers.
 */

function computeDropdownResultAsync<T, U = any>(
  fetcher: DropdownFetcher<T, U>,
  result: DropdownFetchResult<T, U>,
  query: string,
): FilterDropdownResults<T> {
  // Load more async is just a function within a result that can use the items + pagination data
  // to fetch more + create a new result (which will then include another new load more function)
  const loadMoreAsync = async (loadMoreCancelToken: CancelToken) => {
    const loadMoreResult = await fetcher(query, result.items, loadMoreCancelToken, result.paginationData);
    return computeDropdownResultAsync(fetcher, loadMoreResult, query);
  };

  return {
    canLoadMore: result.canLoadMore,
    items: result.items,
    loadMoreAsync,
    query,
    rowCount: result.items.length,
  };
}
